import React, { useMemo, useState } from 'react';
import Modal from 'components/modal';
import { useTranslation } from 'react-i18next';
import { useUpdateNotificationsMutation } from 'redux_store/services/notifications';
import { formatTimeDifferenceUTC, parseUtcDate } from 'constants/updatedAt';
import { getNotificationChipCategory } from 'pages/notification/constant';
import styles from '../../styles/components/notifications-modal.module.css';
import {
  Bell,
  X,
  Check,
  Circle,
  FileText,
  CheckCircle2,
  Mail,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react';

const PAGE_SIZE = 5;

const CATEGORY_META = {
  action: { label: 'Action needed', chipClass: 'chipAction', Icon: FileText },
  success: { label: 'Success', chipClass: 'chipSuccess', Icon: CheckCircle2 },
  update: { label: 'Update', chipClass: 'chipUpdate', Icon: Mail },
};

/**
 * "All notifications" dialog (Figma node 1856:2115), opened from the
 * sidebar panel's "View all notifications" button.
 *
 * Read/unread changes are applied optimistically via local overrides
 * alongside the mark-as-read mutation. "Mark unread" is local-only —
 * the backend PUT only supports marking read.
 */
const NotificationsModal = ({ show, onClose, notifications = [], onNotificationClick }) => {
  const { t } = useTranslation(['common']);
  const [updateNotification] = useUpdateNotificationsMutation();
  const [filter, setFilter] = useState('all');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [overrides, setOverrides] = useState({});

  const readStateOf = (n) => overrides[n.userNotificationId] ?? n.isRead;
  const unreadCount = notifications.filter((n) => readStateOf(n) === '0').length;

  const visible = useMemo(() => {
    const filtered = notifications.filter(
      (n) => filter === 'all' || readStateOf(n) === '0'
    );
    return [...filtered].sort((a, b) => {
      const da = parseUtcDate(a.createdAt);
      const db = parseUtcDate(b.createdAt);
      const diff = (da ? da.getTime() : 0) - (db ? db.getTime() : 0);
      return sortDir === 'desc' ? -diff : diff;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, filter, sortDir, overrides]);

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = visible.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const rangeStart = visible.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(visible.length, currentPage * PAGE_SIZE);

  const selectFilter = (next) => {
    setFilter(next);
    setPage(1);
  };

  const markRead = (notification) => {
    const id = notification.userNotificationId;
    setOverrides((prev) => ({ ...prev, [id]: '1' }));
    updateNotification({ data: { notification_ids: [id] } });
  };

  const markUnread = (notification) => {
    setOverrides((prev) => ({
      ...prev,
      [notification.userNotificationId]: '0',
    }));
  };

  const markAllRead = () => {
    const unreadIds = notifications
      .filter((n) => readStateOf(n) === '0')
      .map((n) => n.userNotificationId);
    if (unreadIds.length === 0) return;
    setOverrides((prev) => ({
      ...prev,
      ...Object.fromEntries(unreadIds.map((id) => [id, '1'])),
    }));
    updateNotification({ data: { notification_ids: unreadIds } });
  };

  return (
    <Modal show={show} onClose={onClose} width="max-w-[652px]">
      <div className={styles.dialog}>
        <div className={styles.header}>
          <Bell className={styles.headerIcon} />
          <h2 className={styles.title}>
            {t('common:allNotifications', { defaultValue: 'All notifications' })}
          </h2>
          <span className={styles.unreadPill}>
            {t('common:unreadCount', {
              defaultValue: '{{count}} unread',
              count: unreadCount,
            })}
          </span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common:buttons.close', {
              defaultValue: 'Close notifications',
              keySeparator: false,
            })}
          >
            <X className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.tabsRow} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'all'}
            className={`${styles.tab} ${filter === 'all' ? styles.tabActive : ''}`}
            onClick={() => selectFilter('all')}
          >
            {t('common:all', { defaultValue: 'All' })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'unread'}
            className={`${styles.tab} ${filter === 'unread' ? styles.tabActive : ''}`}
            onClick={() => selectFilter('unread')}
          >
            {t('common:unread', { defaultValue: 'Unread' })}
          </button>
          <button
            type="button"
            className={styles.tab}
            onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
          >
            {sortDir === 'desc'
              ? t('common:sortNewestFirst', { defaultValue: 'Sort: Newest First' })
              : t('common:sortOldestFirst', { defaultValue: 'Sort: Oldest First' })}
          </button>
        </div>

        <div className={styles.metaRow}>
          <span className={styles.metaText}>
            {filter === 'all'
              ? t('common:showingAllNotifications', {
                  defaultValue: 'Showing all {{count}} notifications',
                  count: visible.length,
                })
              : t('common:showingUnreadNotifications', {
                  defaultValue: 'Showing {{count}} unread notifications',
                  count: visible.length,
                })}
          </span>
          <button
            type="button"
            className={styles.markAllButton}
            onClick={markAllRead}
            disabled={unreadCount === 0}
          >
            {t('common:markAllAsRead', { defaultValue: 'Mark all as read' })}
          </button>
        </div>

        <div className={styles.list}>
          {pageItems.length === 0 && (
            <div className={styles.empty}>
              {t('common:noNewNotifications')}
            </div>
          )}
          {pageItems.map((notification) => {
            const isUnread = readStateOf(notification) === '0';
            const meta =
              CATEGORY_META[getNotificationChipCategory(notification)] ||
              CATEGORY_META.update;
            const ChipIcon = meta.Icon;
            const timeLabel = notification?.createdAt
              ? formatTimeDifferenceUTC(notification.createdAt)
              : '';
            return (
              <div
                key={notification.userNotificationId}
                className={`${styles.row} ${isUnread ? styles.rowUnread : ''}`}
              >
                <div
                  className={`${styles.chip} ${styles[meta.chipClass]} ${
                    isUnread ? '' : styles.chipMutedState
                  }`}
                >
                  <ChipIcon className={styles.chipIcon} />
                </div>
                <div
                  className={styles.rowBody}
                  role={onNotificationClick ? 'button' : undefined}
                  tabIndex={onNotificationClick ? 0 : undefined}
                  style={onNotificationClick ? { cursor: 'pointer' } : undefined}
                  onClick={() => {
                    if (!onNotificationClick) return;
                    if (isUnread) markRead(notification);
                    onNotificationClick(notification);
                  }}
                  onKeyDown={(e) => {
                    if (onNotificationClick && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      if (isUnread) markRead(notification);
                      onNotificationClick(notification);
                    }
                  }}
                >
                  <h4
                    className={isUnread ? styles.rowTitle : styles.rowTitleRead}
                  >
                    {notification.subject}
                  </h4>
                  <p className={styles.rowMessage}>{notification.message}</p>
                  <div className={styles.rowMetaRow}>
                    <span className={styles.rowTime}>{timeLabel}</span>
                    <span className={styles.rowTag}>&middot; {meta.label}</span>
                  </div>
                  {isUnread && notification.ctaLabel && (
                    <button
                      type="button"
                      className={styles.rowCta}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onNotificationClick) {
                          markRead(notification);
                          onNotificationClick(notification);
                        } else {
                          markRead(notification);
                        }
                      }}
                    >
                      {notification.ctaLabel}
                    </button>
                  )}
                </div>
                <div className={styles.rowActions}>
                  {isUnread ? (
                    <button
                      type="button"
                      className={styles.markButton}
                      onClick={() => markRead(notification)}
                    >
                      <Check className={styles.markIcon} />
                      {t('common:markRead', { defaultValue: 'Mark read' })}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.markButton} ${styles.markButtonMuted}`}
                      onClick={() => markUnread(notification)}
                    >
                      <Circle className={styles.markIcon} />
                      {t('common:markUnread', { defaultValue: 'Mark unread' })}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          <span className={styles.footerText}>
            {t('common:showingEntries', {
              defaultValue: 'Showing {{start}}–{{end}} of {{total}} entries',
              start: rangeStart,
              end: rangeEnd,
              total: visible.length,
            })}
          </span>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={() => setPage(1)}
            disabled={currentPage === 1}
            aria-label={t('common:firstPage', { defaultValue: 'First page' })}
          >
            <ChevronsLeft className={styles.pagerIcon} />
          </button>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label={t('common:previousPage', { defaultValue: 'Previous page' })}
          >
            <ChevronLeft className={styles.pagerIcon} />
          </button>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={currentPage === pageCount}
            aria-label={t('common:nextPage', { defaultValue: 'Next page' })}
          >
            <ChevronRight className={styles.pagerIcon} />
          </button>
          <button
            type="button"
            className={styles.pagerButton}
            onClick={() => setPage(pageCount)}
            disabled={currentPage === pageCount}
            aria-label={t('common:lastPage', { defaultValue: 'Last page' })}
          >
            <ChevronsRight className={styles.pagerIcon} />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default NotificationsModal;
